import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { InvoiceCategoryFilter } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { Settlement } from '~hooks/useSettlements/models';
import {
  formatSettlementTotal,
  getFriendlySettlementId,
  getSettlementCounterpartyName,
} from '~utils/settlements/settlement-utils';

interface ExportSettlementConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  settlement?: Settlement;
  category?: InvoiceCategoryFilter;
  isLoading?: boolean;
}

export default function ExportSettlementConfirmationDialog({
  open,
  onClose,
  onConfirm,
  settlement,
  category,
  isLoading,
}: ExportSettlementConfirmationDialogProps) {
  const { currencyFormatter } = useCompanyCurrency();

  if (!settlement) {
    return null;
  }

  const settlementId = getFriendlySettlementId(settlement, category);
  const formattedTotal = formatSettlementTotal(settlement, currencyFormatter);
  const customerName = getSettlementCounterpartyName(settlement);

  return (
    <Dialog isOpen={open} onClose={onClose} maxWidth="xs">
      <DialogHeader onClose={onClose}>
        {t('approvals.driver_pay.settlements.export_to_accounting.title')}
      </DialogHeader>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="error" sx={{ py: 0.5 }}>
            {t('approvals.driver_pay.settlements.export_to_accounting.warning')}
          </Alert>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              bgcolor: (theme) => theme.semantic.surfaceSunken,
              p: 2,
              borderRadius: 1,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('approvals.driver_pay.settlements.settlement_id')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                }}
              >
                {settlementId}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('approvals.driver_pay.settlements.export_to_accounting.customer')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                }}
              >
                {customerName}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('common.total')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'success.main',
                }}
              >
                {formattedTotal}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <SmallButton variant="outlined" color="secondary" onClick={onClose}>
          {t('actions.cancel')}
        </SmallButton>
        <SmallButton
          variant="contained"
          color="primary"
          onClick={onConfirm}
          disabled={isLoading}
        >
          {t('approvals.driver_pay.settlements.export_to_accounting.export_button')}
        </SmallButton>
      </DialogActions>
    </Dialog>
  );
}
